/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1786492800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Aug 12 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "purchasely",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Purchasely",
          "depth": 0,
          "id": "connect-to-purchasely"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Custom audiences",
          "depth": 1,
          "id": "custom-audiences"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Validate your setup",
          "depth": 1,
          "id": "validate-your-setup"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      strong: "strong",
      p: "p",
      ul: "ul",
      li: "li",
      h3: "h3",
      h4: "h4"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported sync modes"
            }), _jsx(_components.th, {
              children: "API reference"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#custom-audiences",
                children: _jsx(_components.strong, {
                  children: "Custom audiences"
                })
              })
            }), _jsx(_components.td, {
              children: "Add or remove users from a Purchasely custom audience"
            }), _jsx(_components.td, {
              children: "Add, Remove"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://docs.purchasely.com/docs/custom-audiences-api",
                children: "Sync user custom audiences"
              })
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-purchasely",
        children: "Connect to Purchasely"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsx(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: "Destinations overview"
        }), " page and select ", _jsx(_components.strong, {
          children: "Add destination"
        }), ". Select ", _jsx(_components.strong, {
          children: "Purchasely"
        }), " from the destination catalog and select ", _jsx(_components.strong, {
          children: "Continue"
        }), ". Then enter the following:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Application ID"
          }), ": the ID of the Purchasely application you want to sync audiences to, found in the Purchasely Console."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "API key"
          }), ": a Client API key, created in the Purchasely Console under ", _jsx(_components.strong, {
            children: "Settings → Client API Keys"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Select ", _jsx(_components.strong, {
          children: "Test connection"
        }), " to verify your credentials."]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "The Custom Audiences API is a gated Purchasely feature. If the connection test\nfails with a message about the feature not being enabled, contact your\nPurchasely account manager to enable custom audiences for your account."
        })
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've connected your Purchasely destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration. Go to the ", _jsx(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: "Syncs overview"
        }), " page and select ", _jsx(_components.strong, {
          children: "Add sync"
        }), ". Then select the relevant model and your Purchasely destination."]
      }), "\n", _jsx(_components.h3, {
        id: "custom-audiences",
        children: "Custom audiences"
      }), "\n", _jsx(_components.p, {
        children: "You can sync to an existing Purchasely custom audience or have Hightouch create a new one:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Use an existing audience"
          }), ": select from the custom audiences already in your Purchasely application."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Create a new audience"
          }), ": Hightouch creates the audience on the first sync run, deriving its Purchasely audience ID from the name you enter."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "user-identifiers",
        children: "User identifiers"
      }), "\n", _jsx(_components.p, {
        children: "Purchasely matches audience members by one of two identifier types:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "User ID"
          }), ": your own user identifier, as passed to the Purchasely SDK."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Anonymous ID"
          }), ": the anonymous identifier generated by the Purchasely SDK."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Select the identifier type and map the model column that contains it. Each Purchasely audience is fixed to a single identifier type: new audiences are created with the type you select, and for existing audiences you should select the type the audience was created with."
      }), "\n", _jsx(_components.h4, {
        id: "removing-users",
        children: "Removing users"
      }), "\n", _jsx(_components.p, {
        children: "When rows leave your model's query results, Hightouch can remove those users from the audience or leave the audience unchanged. Choose the behavior you want in the sync configuration."
      }), "\n", _jsx(_components.h4, {
        id: "how-syncing-works",
        children: "How syncing works"
      }), "\n", _jsx(_components.p, {
        children: "Purchasely applies audience changes asynchronously and processes one change per audience at a time. Hightouch sends membership changes in batches, waits for Purchasely to finish applying each batch, and fails the sync if Purchasely reports that a change couldn't be applied. Large audiences may take several minutes to finish processing on Purchasely's side."
      }), "\n", _jsx(_components.p, {
        children: "Once synced, you can target the custom audience in Purchasely Placements, Campaigns, and A/B tests to show different screens, offers, and messaging to different groups of users."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "validate-your-setup",
        children: "Validate your setup"
      }), "\n", _jsx(_components.p, {
        children: "After your first sync completes, confirm the results in Purchasely:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "In the Purchasely Console, check that the custom audience exists and shows the expected user count."
        }), "\n", _jsx(_components.li, {
          children: "Rows without a value in the mapped identifier column are rejected rather than synced. Check the sync's rejected rows if counts look low."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
